import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactComponent as RedxIcon } from '../../../assets/icons/redX.svg';
import './AgencyCampaign.scss';
import { Button, EButtonType, Input } from '@repo/apg-ui';
import classNames from 'classnames';
import { useNavigate } from 'react-router';
import { ClientSelectInput } from '../../ClientSelectInput';
import SpinnerV2 from '../../SpinnerV2';
import { RootState } from '../../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as _actionsBasket from '../../../actions/basket';
import { connect } from 'react-redux';
import * as _actionsSujet from '../../../actions/sujet';
import * as _actionsFilters from '../../../actions/filters';
import { buildNewCustomerFormUrl } from '../../../utils/newCustomerFormUrl';
import { SchemaIndustry } from '@repo/legacy-commerce-api';

// import { SpinnerV2 } from '@repo/apg-ui';

interface Props {
  onClose?: any;
  onSuccess?: any;
  isEditModal?: boolean;
  userDataEC?: any;
  onAgencyCampaignChange?: any;
  actionsSujet: typeof _actionsSujet;
  actionsBasket: typeof _actionsBasket;
  actionsFilters: typeof _actionsFilters;
  customersForAgency: any;
}

const AgencyCampaignModal = ({
  onClose,
  isEditModal,
  onAgencyCampaignChange,
  customersForAgency,
  actionsSujet,
  actionsBasket,
  actionsFilters,
  userDataEC,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = React.useState<string>('');
  const [customer, setCustomer] = React.useState<any>(null);
  const [isFormDirty, setIsFormDirty] = React.useState<boolean>(false);
  const intl = useIntl();
  const navigate = useNavigate();
  const gepardNr = (window as any).keycloak?.tokenParsed?.['gepard-nr'];
  const newCustomerFormUrl = buildNewCustomerFormUrl(userDataEC?.userInfo, gepardNr, intl.locale);

  useEffect(() => {
    setIsLoading(true);
    actionsSujet.getCustomersForAgency().then((res) => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (userDataEC?.agencyData && userDataEC?.agencyData?.campaignName && isEditModal) {
      setName(userDataEC?.agencyData?.campaignName);
    }

    if (userDataEC?.agencyData && userDataEC?.agencyData?.customer && isEditModal) {
      setCustomer(userDataEC?.agencyData?.customer);
    }
  }, [userDataEC?.agencyData]);

  // The customer only carries its industry group (e.g. "720"), the basket's full industry code comes back flagged as selected
  const selectBasketIndustry = (basket: number, basketreference: string) =>
    actionsFilters
      .fetchIndustries({ context: 'big5', basket, basketreference })
      .then((industries: SchemaIndustry[]) => {
        actionsFilters.updateIndustry(industries?.find((industry) => industry.selected)?.id ?? null);
      });

  const updateAgencyCampaignDetail = () => {
    if (userDataEC.basketInfo) {
      actionsBasket
        .postBasketData({
          name: name,
          basket: userDataEC.basketInfo.basket,
          customer: customer.customerid,
        })
        .then((res) => {
          actionsSujet.setAgencyData({
            basketId: userDataEC.basketInfo.basket,
            campaignName: name,
            customer: customer,
          });
          setIsFormDirty(false);
          selectBasketIndustry(userDataEC.basketInfo.basket, userDataEC.basketInfo.basketreference);
          navigate({ search: '' });
        });
    } else {
      createCampaignWithNewBasket();
    }
  };

  const createCampaignWithNewBasket = () => {
    actionsBasket
      .createBasket({
        name: name,
        branche: customer.brcid,
        customer: customer.customerid,
        customercontact: customer.customercontactid,
      })
      .then((res) => {
        actionsSujet.setIsAgencyMode(true);
        actionsSujet.setAgencyInfoPanelVisibility(true);
        actionsSujet.setAgencyData({
          basketId: res.basket,
          campaignName: name,
          customer: customer,
        });
        selectBasketIndustry(res.data.basket, res.data.basketreference);

        if (isEditModal) {
          setIsFormDirty(false);
          navigate({ search: '' });
        } else {
          onAgencyCampaignChange(name, customer);
        }
      });
  };

  return (
    <div
      className={classNames('agencyCampaignModal', {
        modalLoading: isLoading,
      })}
      id='root'
    >
      <div className={classNames('modalHeader border-bottom d-flex justify-content-between align-items-center h-auto')}>
        <div className='d-flex flex-column justify-content-center gap-2'>
          <div className='font-34 font-bold title-text'>
            {!isEditModal && (
              <FormattedMessage id='ec.agency.campaign.modal.createHeader' defaultMessage='Neue Kampagne erfassen' />
            )}
            {isEditModal && (
              <FormattedMessage
                id='ec.agency.campaign.modal.updateHeader'
                defaultMessage='Kunde und/oder Kampagnenname ändern'
              />
            )}
          </div>
        </div>

        <div className='close-modal'>
          <Button onClick={onClose} className='mt-0'>
            <RedxIcon />
          </Button>
        </div>
      </div>

      <div className={classNames('modalBody h-100')}>
        <div className={'row m-4'}>
          <label htmlFor='campaignNameInput' className='input-label'>
            {intl.formatMessage({
              id: 'ec.agency.campaign.modal.name.label',
              defaultMessage: 'Kampagnenname eingeben',
            })}
          </label>
          <Input
            id='campaignNameInput'
            value={name}
            name={'name'}
            onChange={(name, value) => {
              setName(value);
              setIsFormDirty(true);
            }}
            type='text'
            placeholder={intl.formatMessage({
              id: 'ec.agency.campaign.modal.name.placeholder',
              defaultMessage: 'Kampagnenname',
            })}
            inputWrapperClass='high col-12'
          />
        </div>
        <div className={'row m-4'}>
          <label htmlFor='campaignClientInput' className='input-label'>
            {intl.formatMessage({
              id: 'ec.agency.campaign.modal.client.label',
              defaultMessage: 'Kunde auswählen',
            })}
          </label>
          {isLoading ? (
            <SpinnerV2 relative />
          ) : (
            <ClientSelectInput
              values={customersForAgency}
              onChange={(value) => {
                setCustomer(value);
                setIsFormDirty(true);
              }}
              inputPlaceholder={intl.formatMessage({
                id: 'ec.agency.campaign.modal.client.placeholder',
                defaultMessage: 'Auswählen',
              })}
              inputLabel={''}
              initialValue={customer}
            />
          )}
        </div>

        {newCustomerFormUrl && (
          <div className={'row m-4 below-element-link'}>
            <a href={newCustomerFormUrl} target='_blank' rel='noreferrer'>
              <p className='mb-0 color-red'>
                <FormattedMessage id='ec.agency.campaign.modal.newCustomerLink' defaultMessage='Neuer Kunde' />
              </p>
            </a>
          </div>
        )}
      </div>

      <div className={classNames('modalFooter border-top d-flex w-100 h-auto bg-white flex-column')}>
        <div className='d-flex align-items-center justify-content-end'>
          {isEditModal && (
            <div className='d-flex gap-4 update-actions'>
              <Button
                className='actions-button mt-0'
                onClick={() => {
                  navigate({ search: '' });
                }}
                buttonType={EButtonType.PillLarge}
              >
                <FormattedMessage id='ec.agency.campaign.modal.cancelButton' defaultMessage='Abbrechen' />
              </Button>

              <Button
                isDisabled={!name || name === '' || !customer || customer === '' || !isFormDirty}
                className='actions-button mt-0'
                onClick={() => {
                  updateAgencyCampaignDetail();
                }}
                buttonType={EButtonType.PillLarge}
              >
                <FormattedMessage id='ec.agency.campaign.modal.saveButton' defaultMessage='Speichern' />
              </Button>
            </div>
          )}

          {!isEditModal && (
            <Button
              isDisabled={!name || name === '' || !customer || customer === ''}
              className='actions-button mt-0'
              onClick={() => {
                createCampaignWithNewBasket();
              }}
              buttonType={EButtonType.PillLarge}
            >
              <FormattedMessage id='ec.agency.campaign.modal.continueButton' defaultMessage='Fortfahren' />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    location: state.router.location,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actionsSujet: bindActionCreators(_actionsSujet as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    actionsFilters: bindActionCreators(_actionsFilters as any, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AgencyCampaignModal);
